import { getResData, httpClient } from "utils";
import {
    AddScorecardCellDataRequest,
    AddScorecardCellDataResponse,
    ArchiveScorecardRowRequest,
    ArchiveScorecardRowResponse,
    CreateFromTemplateRequest,
    CreateScorecardRowRequest,
    CreateScorecardRowResponse,
    CreateTemplateFromSiteRequest,
    KPISummaryExportRequest,
    KPISummaryItem,
    KPISummaryRequest,
    ReviewScorecardDataResponse,
    ScorecardConfigResponse,
    ScorecardDataCellDetails,
    ScorecardDataItem,
    ScorecardDataParams,
    ScorecardDocument,
    ScorecardDownloadParams,
    ScorecardNotes,
    ScorecardTemplate,
    ScorecardUploadParams,
    UpdateScorecardCellDataRequest,
    UpdateScorecardCellDataResponse,
    UpdateScorecardRowRequest,
    UpdateScorecardRowResponse,
    UploadTemplateRequest,
    UpdateSiteFurtherInfoRequest,
    UpdateSiteFurtherInfoResponse,
} from "./types";
import { SCORECARD_API_PATHS } from "./consts";

export const getScorecardData = (params: ScorecardDataParams): Promise<ScorecardDataItem[]> =>
    httpClient.post<ScorecardDataItem[]>(SCORECARD_API_PATHS.DATA(params.siteId), {
        dateRanges: params.dateRanges,
        timeframe: params.timeframe,
        unit: params.unit
    }).then(getResData);

export const getScorecardConfig = (): Promise<ScorecardConfigResponse> =>
    httpClient.get<ScorecardConfigResponse>(SCORECARD_API_PATHS.SCORECARD_CONFIG).then(getResData);

export const createScorecardRow = (data: CreateScorecardRowRequest): Promise<CreateScorecardRowResponse> =>
    httpClient.post<CreateScorecardRowResponse>(SCORECARD_API_PATHS.SCORECARD_ROWS, data).then(getResData);

export const updateScorecardRow = (rowId: number, data: UpdateScorecardRowRequest): Promise<UpdateScorecardRowResponse> =>
    httpClient.put<UpdateScorecardRowResponse>(SCORECARD_API_PATHS.SCORECARD_ROW(rowId), data).then(getResData);

export const deleteScorecardRowData = (rowId: number): Promise<void> =>
    httpClient.delete(SCORECARD_API_PATHS.SCORECARD_ROW(rowId));

export const archiveScorecardRow = (rowId: number, query: ArchiveScorecardRowRequest): Promise<ArchiveScorecardRowResponse> =>
    httpClient.post<ArchiveScorecardRowResponse>(SCORECARD_API_PATHS.ARCHIVE_ROW(rowId, query)).then(getResData);

export const downloadScorecard = (params: ScorecardDownloadParams): Promise<Blob> =>
    httpClient.post(SCORECARD_API_PATHS.DOWNLOAD(params.siteId), {
        siteId: params.siteId,
        dateRanges: params.dateRanges,
        timeframe: params.timeframe,
        unit: params.unit
    }, { responseType: 'blob' }).then((response) => response.data);

export const uploadScorecard = (params: ScorecardUploadParams): Promise<void> => {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('siteId', params.siteId.toString());

    return httpClient.post(SCORECARD_API_PATHS.UPLOAD, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    }).then(getResData);
};

// Template API functions
export const getScorecardTemplates = (): Promise<ScorecardTemplate[]> =>
    httpClient.get<ScorecardTemplate[]>(SCORECARD_API_PATHS.TEMPLATES).then(getResData);

export const createTemplateFromSite = (siteId: number, data: CreateTemplateFromSiteRequest): Promise<ScorecardTemplate> =>
    httpClient.post<ScorecardTemplate>(SCORECARD_API_PATHS.CREATE_TEMPLATE_FROM_SITE(siteId), data).then(getResData);

export const deleteScorecardTemplate = (id: number): Promise<void> =>
    httpClient.delete(SCORECARD_API_PATHS.TEMPLATE(id)).then(getResData);

export const createScorecardFromTemplate = (data: CreateFromTemplateRequest): Promise<void> =>
    httpClient.post(SCORECARD_API_PATHS.CREATE_FROM_TEMPLATE, data).then(getResData);

export const downloadScorecardTemplate = (id: number): Promise<Blob> =>
    httpClient.get(SCORECARD_API_PATHS.DOWNLOAD_TEMPLATE(id), { responseType: 'blob' }).then((response) => response.data);

export const uploadScorecardTemplate = (params: UploadTemplateRequest): Promise<void> => {
    const formData = new FormData();
    formData.append('file', params.file);

    const requestData = {
        siteIds: params.siteIds,
        templateName: params.templateName
    };

    // Create a Blob with JSON content type for the request field
    const requestBlob = new Blob([JSON.stringify(requestData)], {
        type: 'application/json'
    });
    formData.append('request', requestBlob);

    return httpClient.post(SCORECARD_API_PATHS.UPLOAD_TEMPLATE, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    }).then(getResData);
};

export const getKPISummary = (params: KPISummaryRequest): Promise<KPISummaryItem[]> =>
    httpClient.post<KPISummaryItem[]>(SCORECARD_API_PATHS.KPI_SUMMARY, params).then(getResData);

export const exportKPISummary = (params: KPISummaryExportRequest): Promise<Blob> =>
    httpClient.post(SCORECARD_API_PATHS.KPI_SUMMARY_EXPORT, params, { responseType: 'blob' }).then((response) => response.data);

export const updateScorecardNotes = (dataId: number, notes: string): Promise<ScorecardNotes> =>
    httpClient.put<ScorecardNotes>(SCORECARD_API_PATHS.NOTES(dataId), { notes }).then(getResData);

export const getScorecardDocuments = (dataId: number): Promise<ScorecardDocument[]> =>
    httpClient.get<ScorecardDocument[]>(SCORECARD_API_PATHS.DOCUMENTS(dataId)).then(getResData);

export const uploadScorecardDocuments = (dataId: number, files: File[]): Promise<ScorecardDocument[]> => {
    const formData = new FormData();
    files.forEach(file => {
        formData.append('file', file);
    });

    return httpClient.post<ScorecardDocument[]>(SCORECARD_API_PATHS.DOCUMENTS(dataId), formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    }).then(getResData);
};

export const deleteScorecardDocument = (documentId: number): Promise<void> =>
    httpClient.delete(SCORECARD_API_PATHS.DOCUMENT(documentId));

export const getSiteFurtherInfo = (siteId: number): Promise<string> =>
    httpClient.get<string>(SCORECARD_API_PATHS.SITE_FURTHER_INFO(siteId)).then(getResData);

export const updateSiteFurtherInfo = (data: UpdateSiteFurtherInfoRequest): Promise<UpdateSiteFurtherInfoResponse> =>
    httpClient.put<UpdateSiteFurtherInfoResponse>(SCORECARD_API_PATHS.UPDATE_SITE_FURTHER_INFO, data).then(getResData);

export const addScorecardCellData = (data: AddScorecardCellDataRequest): Promise<AddScorecardCellDataResponse> =>
    httpClient.post<AddScorecardCellDataResponse>(SCORECARD_API_PATHS.ADD_CELL_DATA, data).then(getResData);

export const updateScorecardCellData = (id: number, data: UpdateScorecardCellDataRequest): Promise<UpdateScorecardCellDataResponse> =>
    httpClient.put<UpdateScorecardCellDataResponse>(SCORECARD_API_PATHS.UPDATE_CELL_DATA(id), data).then(getResData);

export const reviewScorecardDataCell = (scorecardDataId: number): Promise<ReviewScorecardDataResponse> =>
    httpClient.post<ReviewScorecardDataResponse>(SCORECARD_API_PATHS.REVIEW_DATA_CELL(scorecardDataId)).then(getResData);

export const unreviewScorecardDataCell = (scorecardDataId: number): Promise<ReviewScorecardDataResponse> =>
    httpClient.post<ReviewScorecardDataResponse>(SCORECARD_API_PATHS.UNREVIEW_DATA_CELL(scorecardDataId)).then(getResData);

export const getScorecardDataCellDetails = (scorecardDataId: number): Promise<ScorecardDataCellDetails> =>
    httpClient.get<ScorecardDataCellDetails>(SCORECARD_API_PATHS.DATA_CELL_DETAILS(scorecardDataId)).then(getResData);

export const deleteScorecardDataCell = (scorecardDataId: number): Promise<void> =>
    httpClient.delete(SCORECARD_API_PATHS.DATA_CELL_DETAILS(scorecardDataId));